import React from 'react';

//standard library imports
import { useEffect, useState, useContext } from 'react';
//External library imports
import { get as lodashGet, set as lodashSet, keys as lodashKeys } from 'lodash';
import { toast } from 'react-toastify';
import moment from 'moment';
import { UilCopy, UilInfoCircle, UilImport, UilTrash, UilCapture, UilSync, UilKeyboardShow, UilBan, UilNotes, UilBoltSlash, UilLayerGroup } from '@iconscout/react-unicons';
import { MenuItem, Menu, MenuDivider } from '@szhsin/react-menu';
//Internal module imports
import { useLoader, useQueryParamWithDefault } from '../../../../hooks';
import Typography from '../../../../components/Typography/Typography';
import Table from '../../../../components/Table/Table';
import ModalComponent from '../../../../components/ModalComponent/ModalComponent';
import DatePicker from '../../../../components/Inputs/DatePicker/DatePicker';
import { DropdownComponent } from '../../../../components/Inputs/Input';
import FilterBarClasses from '../../../../styles/Filterbar.module.css';
import { AdminService } from '../../../../services/BiddingService/CommonServices/AdminService';
import { CustomerService } from '../../../../services/BiddingService/CommonServices/CustomerService';
import { RegulationService } from '../../../../services/BiddingService/CommonServices/RegulationService';
import { momentTimeFormater } from '../../../../utils/timeHelper';
import bidClasses from '../Bidding.module.css';
import IconComponent from '../../../../components/Icon/Icon';
import BiddingInfo from '../ModalComponent/BidInfo';
import DeleteModal from '../../../../components/DeleteModal/DeleteModal';
import ResultId from '../ModalComponent/ResultId';
import { DownloadAsExcel } from '../../../../utils/downloadAsExcel';
import { NotesLogsIndex } from '../ModalComponent/Note';
import FetchResult from '../ModalComponent/FetchResult';
import { Panic } from '../ModalComponent/Panic';
import { AdminBiddingService } from '../../../../services/BiddingService/Admin/BiddingService';
import { useUserRole } from '../../../../hooks/use-user-role.hook';
import DateRangePicker from '../../../../components/Inputs/DateRangePicker/DateRangePicker';
import { dkCurrencyFormat } from '../../../../utils/currencyHelper';
import { UpdatePostedEnergyBid } from '../ModalComponent/UpdatePostedEnergyBid';
import { RootBundleBiddingService } from '../../../../services/BiddingService/Root/BundleBiddingService';
import { CreateBundleBid } from './ModalComponent/CreateBundleBid';
import { CreateSecondaryBundleBid } from './ModalComponent/CreateSecondaryBundleBid';
import { CreateBulkBundleBid } from './ModalComponent/CreateBulkBundleBid';
import { AdminBundleBiddingService } from '../../../../services/BiddingService/Admin/BundleBiddingService';
import { AdminInstanceBundleService } from '../../../../services/BiddingService/Admin/InstanceBundleService';
import { CreateEditInstanceBundle } from './ModalComponent/CreateEditInstanceBundle';
import { AuthContext } from '../../../../context/AuthContext';
import { checkPartnerBidAccess } from '../../../../utils/permissionHelper';
import { DateParam, StringParam } from 'use-query-params';

const DEFAULT_DROPDOWN_OPTION = {
    label: 'All',
    value: 'all',
};

const getDropdownOption = (data = [], isDefaultOption = true, labelKey = 'name', valueKey = '_id') => {
    const temp = isDefaultOption ? [DEFAULT_DROPDOWN_OPTION] : [];
    data.map((el) => {
        temp.push({ label: lodashGet(el, labelKey), value: lodashGet(el, valueKey) });
    });
    return temp;
};

const FILTER_OPTIONS = [
    { label: 'All Bids', value: 'all' },
    { label: 'No Bids', value: 'noBids' },
];

const HEADERS_KEYS = {
    masterBid: {
        afrr: {
            headers: ['Customer Name', 'Bundle Name', 'Avg Capacity', 'Revenue CM (€)', 'Revenue EAM (€)', 'Status CM/EAM', 'Action'],
            keys: ['customerName', 'instanceBundleName', 'averageCapacity', 'revenue', 'energyRevenue', 'status', 'action'],
        },
        fcr: {
            headers: ['Customer Name', 'Bundle Name', 'Avg Capacity', 'Revenue CM (€)', 'Status (Primary/Secondary)', 'Action'],
            keys: ['customerName', 'instanceBundleName', 'averageCapacity', 'revenue', 'status', 'action'],
        },
    },
    indivisualMarket: {
        afrr: {
            headers: ['Date', 'Avg Capacity', 'Revenue CM (€)', 'Revenue EAM (€)', 'Status CM/EAM', 'Action'],
            keys: ['cetDate', 'averageCapacity', 'revenue', 'energyRevenue', 'status', 'action'],
        },
        fcr: {
            headers: ['Date', 'Avg Capacity', 'Revenue CM (€)', 'Status (Primary/Secondary)', 'Action'],
            keys: ['cetDate', 'averageCapacity', 'revenue', 'status', 'action'],
        },
    },
};

export const BundleBid = ({ heading, indivisualMarket = { status: false, market: null }, cId }) => {
    const [initialLoad, setInitialLoad] = useState(false);

    const { state } = useContext(AuthContext);
    const { isRootUser, isAdminUser, adminId } = useUserRole();
    const service = isRootUser ? RootBundleBiddingService : AdminBundleBiddingService;
    const [startLoader, stopLoader] = useLoader();

    const [date, setDate] = useQueryParamWithDefault('date', DateParam, indivisualMarket.status ? undefined : moment().toDate(), '', { clearOnUnmount: false });

    const [admins, setAdmins] = useState([isAdminUser ? {} : DEFAULT_DROPDOWN_OPTION]);
    const [selectedAdmin, setSelectedAdmin] = useQueryParamWithDefault('admin', StringParam, isAdminUser ? adminId : DEFAULT_DROPDOWN_OPTION.value, '', {
        clearOnUnmount: false,
    });

    const [customers, setCustomers] = useState(indivisualMarket.status ? [] : [DEFAULT_DROPDOWN_OPTION]);
    const [selectedCustomer, setSelectedCustomer] = useQueryParamWithDefault('customer', StringParam, indivisualMarket.status ? {} : DEFAULT_DROPDOWN_OPTION.value, '', {
        clearOnUnmount: false,
    });

    const [regulations, setRegulations] = useState([]);
    const [selectedRegulation, setSelectedRegulation] = useQueryParamWithDefault('market', StringParam, indivisualMarket.status ? indivisualMarket.market : '', '', {
        clearOnUnmount: false,
    });

    const [selectedFilter, setSelectedFilter] = useQueryParamWithDefault('filter', StringParam, FILTER_OPTIONS[0].value, cId, { clearOnUnmount: true });

    // for indivisual market bids on admin
    const [instanceBundles, setInstanceBundles] = useState([]);
    const [selectedInstanceBundle, setSelectedInstanceBundle] = useState({});

    const [startDate, setStartDate] = useQueryParamWithDefault('startDate', DateParam, indivisualMarket.status ? moment().startOf('month').toDate() : undefined, '', {
        clearOnUnmount: false,
    });
    const [endDate, setEndDate] = useQueryParamWithDefault('endDate', DateParam, indivisualMarket.status ? moment().endOf('month').toDate() : undefined, '', {
        clearOnUnmount: false,
    });

    const [agreement, setAgreement] = useState({});
    const [totalRevenue, setTotalRevenue] = useState({ capacity: 0, energy: 0 });
    //-----------//

    const [tableHeadersAndKeys, setTableHeadersAndKeys] = useState({
        headers: [],
        keys: [],
    });
    const [paginationQuery, setPaginationQuery] = useState({
        skip: 0,
        page: 0,
        limit: 15,
    });
    const [bidsData, setBidsData] = useState({
        bids: [],
        totalCount: 0,
    });

    const [modal, setModal] = useState({
        status: false,
        modalId: null,
        data: {},
    });
    const [deleteModal, setDeleteModal] = useState({
        status: false,
        source: null,
        type: null,
        id: null,
    });
    const [marketEnabledCustomerLicenseIns, setMarketEnabledCustomerLicenseIns] = useState({});

    // partner with edit permission
    const [editAccess, setEditAccess] = useState(true);

    useEffect(() => {
        if (document.readyState === 'complete') setInitialLoad(true);
    }, []);

    // for indivisual market bids on admin

    useEffect(() => {
        if (indivisualMarket.status) {
            if (selectedRegulation !== indivisualMarket.market) setSelectedCustomer('');

            setSelectedRegulation(indivisualMarket.market);
            if (state) setEditAccess(checkPartnerBidAccess(state, indivisualMarket.market));
        }
    }, [indivisualMarket.market]);

    useEffect(() => {
        if (indivisualMarket.status && selectedCustomer) getInstanceBundle();
    }, [selectedCustomer]);

    // ------------- //

    useEffect(() => {
        if (isRootUser && !indivisualMarket.status) getAdmins();
        if (!indivisualMarket.status) getRegulation();
    }, []);

    useEffect(() => {
        if (selectedAdmin && selectedAdmin !== 'all' && !indivisualMarket.status) getCustomers();
        else if (selectedAdmin === 'all') {
            setCustomers([DEFAULT_DROPDOWN_OPTION]);
            setSelectedCustomer(DEFAULT_DROPDOWN_OPTION.value);
        }
    }, [selectedAdmin]);

    useEffect(() => {
        const key = !indivisualMarket?.status ? 'masterBid' : 'indivisualMarket';
        if (selectedRegulation?.includes('aFRR') || selectedRegulation?.includes('mFRR')) setTableHeadersAndKeys(lodashGet(HEADERS_KEYS, [key, 'afrr']));
        else setTableHeadersAndKeys(lodashGet(HEADERS_KEYS, [key, 'fcr']));

        if (indivisualMarket.status) getMarketEnabledCusLicenseIns();
    }, [selectedRegulation]);

    useEffect(() => {
        if ((selectedCustomer && selectedRegulation && !indivisualMarket.status) || (indivisualMarket.status && selectedInstanceBundle?.value && selectedCustomer)) getBids();
    }, [paginationQuery.skip, selectedCustomer, selectedRegulation, selectedInstanceBundle?.value]);

    const getRegulation = () => {
        const params = { date: momentTimeFormater(date).startOf('day').format() };
        RegulationService.GetRegulationSortedOnBids(params, startLoader, handleGetRegulationSuccess, handleError, stopLoader);
    };

    const handleGetRegulationSuccess = ({ data }) => {
        const options = getDropdownOption(data.data, false, 'name', 'name');
        if (!selectedRegulation) setSelectedRegulation(options[0].value);
        setRegulations(options);
    };

    const getAdmins = () => {
        AdminService.GetAdmins(startLoader, handleGetAdminSuccess, handleError, stopLoader);
    };

    const handleGetAdminSuccess = ({ data }) => {
        const options = getDropdownOption(data.data);
        if (!selectedAdmin) setSelectedAdmin(options[0].value);

        setAdmins(options);
    };

    const getCustomers = () => {
        const params = { adminId: selectedAdmin };
        CustomerService.GetCustomers(params, startLoader, handleGetCustomerSuccess, handleError, stopLoader);
    };

    const handleGetCustomerSuccess = ({ data }) => {
        const options = getDropdownOption(data.data);
        if (!selectedCustomer || initialLoad) setSelectedCustomer(options[0].value);

        setCustomers(options);
    };

    const getBids = () => {
        if (!indivisualMarket.status && selectedRegulation) {
            const params = {
                limit: paginationQuery.limit,
                skip: paginationQuery.skip,
                market: selectedRegulation,
                bidDate: moment(date).format('YYYY-MM-DD'),
                adminId: selectedAdmin,
                customerId: selectedCustomer,
                filter: selectedFilter,
            };
            service.GetMasterBids(params, startLoader, handleBidsSuccess, handleError, stopLoader);
        } else {
            const params = {
                startDate: moment(startDate).format('YYYY-MM-DD'),
                endDate: moment(endDate).format('YYYY-MM-DD'),
                instanceBundleId: selectedInstanceBundle?.value,
                limit: paginationQuery.limit,
                skip: paginationQuery.skip,
                market: selectedRegulation,
            };
            AdminBundleBiddingService.GetBundleBids(params, startLoader, handleBidsSuccess, handleError, stopLoader);
        }
    };

    const handleNull = (value) => (value || value === 0 ? (!isNaN(value) ? dkCurrencyFormat(value) : value) : '--');

    const handleExport = (bid) => {
        if (selectedRegulation?.includes('FCR-D')) {
            let totalPrimaryPrice = 0;
            let totalSecondaryPrice = 0;
            let totalPrice = 0;

            const data = bid.capacity.map((cap, index) => {
                const primarySettlementPrice = lodashGet(bid, ['primary', 'provider', 'bidStatus', index, 'price'], 0);
                const secondarySettlementPrice = lodashGet(bid, ['secondary', 'provider', 'bidStatus', index, 'price'], 0);
                const totalSettlementPrice = lodashGet(bid, ['economics', 'hourlyData', index, 'price'], 0);

                totalPrimaryPrice += primarySettlementPrice;
                totalSecondaryPrice += secondarySettlementPrice;
                totalPrice += totalSettlementPrice;

                return {
                    hour: index,
                    primaryCapacity: lodashGet(bid, ['primary', 'capacity', index], '--'),
                    primaryPrice: lodashGet(bid, ['primary', 'price', index], '--'),
                    primarySettlement: primarySettlementPrice,
                    secondaryCapacity: lodashGet(bid, ['secondary', 'capacity', index], '--'),
                    secondaryPrice: lodashGet(bid, ['secondary', 'price', index], '--'),
                    secondarySettlement: secondarySettlementPrice,
                    hourcost: totalSettlementPrice,
                };
            });

            data.push({
                hour: 'Total',
                primaryCapacity: '',
                primaryPrice: '',
                primarySettlement: totalPrimaryPrice,
                secondaryCapacity: '',
                secondaryPrice: '',
                secondarySettlement: totalSecondaryPrice,
                hourcost: totalPrice,
            });

            DownloadAsExcel(data, `${selectedRegulation}_${momentTimeFormater(bid?.date).format('YYYY-MM-DD')}`, [
                'Hour',
                'Capacity (MW) (Primary)',
                'Price (€) (Primary)',
                'Settlement (€) (Primary)',
                'Capacity (MW) (Secondary)',
                'Pricem (€) (Secondary)',
                'Selttlement (€) (Secondary)',
                'Settlement (€) ( Primary & Secondary )',
            ]);
        } else {
            const data = lodashGet(bid, ['primary', 'capacity'], []).map((cap, index) => ({
                hour: index,
                capacity: cap,
                price: lodashGet(bid, ['primary', 'price', index], '--'),
                hourcost: lodashGet(bid, ['bidSattlement', index, 'price'], '--'),
            }));
            DownloadAsExcel(data, `${selectedRegulation}_${momentTimeFormater(bid?.date).format('YYYY-MM-DD')}`, ['Hour', 'Capacity', 'Price', 'Settlement']);
        }
    };

    const handleBidsSuccess = ({ data }) => {
        const processedData = lodashGet(data, ['data', 'data'], []).map((item) => {
            if (indivisualMarket.status) item.cetDate = momentTimeFormater(item.date).format('YYYY-MM-DD');
            if (!item.isBidPresent) item.style = { background: '#FFE3E3', color: '#E25657' };
            else if (!lodashGet(item, ['bundleBid', 'settlement'], []).length && !lodashGet(item, ['bundleBid', 'energySettlement'], []).length)
                item.style = { background: '#E6E8FA', color: '#4C5AD9' };

            item.averageCapacity = handleNull(item.averageCapacity);
            item.status = `${handleNull(item.primaryStatus)}/${handleNull(item.secondaryStatus)}`;

            item.revenue = handleNull(item.revenue);

            if (selectedRegulation.includes('aFRR') || selectedRegulation.includes('mFRR')) {
                item.energyRevenue = handleNull(item.energyRevenue);
                const isInactive = lodashGet(item, ['bundleBid', 'primary', 'energyRequestBidStatus'], []).some((el) => el === 'inactive');
                if (isInactive) item.style = { background: '#fef6e5', color: '#faa500' };
            }

            const isPrimaryDeleteDisabled = lodashGet(item, ['bundleBid', 'primary', 'provider', 'bidStatus'], []).length
                ? true
                : (selectedRegulation?.includes('aFRR') || selectedRegulation?.includes('mFRR')) &&
                  lodashGet(item, ['bundleBid', 'primary', 'energyRequestBidStatus'], []).some((el) => el === 'active');

            item.action = (
                <div className={bidClasses.ActionContainer}>
                    {!item.isBidPresent && editAccess ? (
                        <div
                            onClick={() => {
                                if (item.isAgreement) {
                                    setModal({
                                        status: true,
                                        modalId: 'createBid',
                                        data: {
                                            ...(indivisualMarket.status
                                                ? {
                                                      instanceBundle: {
                                                          label: selectedInstanceBundle.label,
                                                          value: selectedInstanceBundle.value,
                                                          pairs: selectedInstanceBundle.pairs || [],
                                                      },
                                                  }
                                                : {
                                                      instanceBundle: {
                                                          label: item.instanceBundleName,
                                                          value: item.instanceBundleId,
                                                          pairs: item.pairs || [],
                                                      },
                                                  }),
                                            market: selectedRegulation,
                                            bidDate: date,
                                            ...(selectedRegulation?.includes('aFRR') || selectedRegulation?.includes('mFRR')
                                                ? {
                                                      cost: item?.cost,
                                                  }
                                                : {}),
                                        },
                                    });
                                } else toast.error('No Agreement Found');
                            }}
                        >
                            <IconComponent icon={UilCopy} tooltip={'Create Bid'} />
                        </div>
                    ) : (
                        <>
                            <div
                                onClick={() => {
                                    setModal({
                                        status: true,
                                        modalId: 'bidInfo',
                                        data: {
                                            capacity: {
                                                overall: lodashGet(item, ['bundleBid', 'capacity'], []),
                                                pairs: indivisualMarket.status ? selectedInstanceBundle?.pairs : item.pairs,
                                                indivisualBids: lodashGet(item, ['bundleBid', 'bids'], []),
                                                panicHours: lodashGet(item, ['bundleBid', 'panic', 'hours'], []),
                                            },
                                            price: {
                                                overall: lodashGet(item, ['bundleBid', 'price'], []),
                                                primary: lodashGet(item, ['bundleBid', 'primary', 'price'], []),
                                                secondary: lodashGet(item, ['bundleBid', 'secondary', 'price'], []),
                                                ...(lodashGet(item, ['bundleBid', 'market'], '').includes('aFRR') || lodashGet(item, ['bundleBid', 'market'], '').includes('mFRR')
                                                    ? {
                                                          regulationPrice: lodashGet(item, ['bundleBid', 'primary', 'regulationPrice'], []),
                                                          energyRequestBidStatus: lodashGet(item, ['bundleBid', 'primary', 'energyRequestBidStatus'], []),
                                                          energyStatusEffectiveFrom: lodashGet(item, ['bundleBid', 'primary', 'energyStatusEffectiveFrom'], null),
                                                      }
                                                    : {}),
                                            },
                                            settlement: {
                                                bidStatus: lodashGet(item, ['bundleBid', 'settlement'], []),
                                                energyStatus: lodashGet(item, ['bundleBid', 'energySettlement'], []),
                                            },
                                            bidDate: lodashGet(item, ['bundleBid', 'date']),
                                        },
                                    });
                                }}
                            >
                                <IconComponent icon={UilInfoCircle} tooltip={'Bid Info'} />
                            </div>
                            <div
                                onClick={() => {
                                    handleExport(item.bundleBid);
                                }}
                            >
                                <IconComponent icon={UilImport} tooltip={'Download'} />
                            </div>
                            {editAccess && ( // partner with edit permission only can view action
                                <>
                                    {(selectedRegulation?.includes('FCR-D') || selectedRegulation?.includes('FCR-N')) &&
                                        lodashGet(item, ['bundleBid', 'secondary', 'isDeleted']) && (
                                            <div
                                                onClick={() => {
                                                    setModal({
                                                        status: true,
                                                        modalId: 'createSecondaryBid',
                                                        data: {
                                                            instanceBundle: {
                                                                value: indivisualMarket.status ? selectedInstanceBundle.value : item.instanceBundleId,
                                                                pairs: indivisualMarket.status ? selectedInstanceBundle?.pairs : item.pairs,
                                                            },
                                                            bundleBid: item.bundleBid,
                                                        },
                                                    });
                                                }}
                                            >
                                                <IconComponent icon={UilSync} tooltip={'Secondary Bid'} />
                                            </div>
                                        )}

                                    {!selectedRegulation?.includes('aFRR') && !selectedRegulation?.includes('mFRR') && (
                                        <>
                                            <div
                                                onClick={() => {
                                                    setModal({
                                                        status: true,
                                                        modalId: 'fetchResult',
                                                        data: item.bundleBid,
                                                    });
                                                }}
                                            >
                                                <IconComponent icon={UilCapture} tooltip={'Fetch Result'} />
                                            </div>

                                            <div
                                                onClick={() => {
                                                    setModal({
                                                        status: true,
                                                        modalId: 'resultId',
                                                        data: item.bundleBid,
                                                    });
                                                }}
                                            >
                                                <IconComponent icon={UilKeyboardShow} tooltip={'Result ID'} />
                                            </div>
                                        </>
                                    )}

                                    {(selectedRegulation?.includes('aFRR') || selectedRegulation?.includes('mFRR')) && (
                                        <div
                                            onClick={() => {
                                                setModal({
                                                    status: true,
                                                    modalId: 'updateEnergyBid',
                                                    data: item.bundleBid,
                                                });
                                            }}
                                        >
                                            <IconComponent icon={UilBoltSlash} tooltip={'Update energy bid'} />
                                        </div>
                                    )}

                                    <Menu
                                        menuButton={
                                            <div>
                                                <IconComponent icon={UilTrash} tooltip={'Delete'} />
                                            </div>
                                        }
                                        direction={'left'}
                                        arrow={true}
                                    >
                                        {lodashGet(item, ['bundleBid', 'primary', 'capacity'], []).length ? (
                                            <>
                                                <MenuItem
                                                    disabled={isPrimaryDeleteDisabled}
                                                    onClick={() => {
                                                        setDeleteModal({ status: true, source: 'bid', type: 'primary', id: item.bundleBid._id });
                                                    }}
                                                >
                                                    Delete Primary Bid {lodashGet(item, ['bundleBid', 'primary', 'provider', 'bidStatus'], []).length ? '(Result Fetched)' : ''}
                                                </MenuItem>
                                                {lodashGet(item, ['bundleBid', 'secondary', 'capacity'], []).length ? <MenuDivider /> : ''}
                                            </>
                                        ) : (
                                            ''
                                        )}

                                        {lodashGet(item, ['bundleBid', 'secondary', 'capacity'], []).length ? (
                                            <MenuItem
                                                disabled={lodashGet(item, ['bundleBid', 'secondary', 'provider', 'bidStatus'], []).length ? true : false}
                                                onClick={() => {
                                                    setDeleteModal({ status: true, source: 'bid', type: 'secondary', id: item.bundleBid._id });
                                                }}
                                            >
                                                <div>
                                                    Delete Secondary Bid {lodashGet(item, ['bundleBid', 'secondary', 'provider', 'bidStatus'], []).length ? '(Result Fetched)' : ''}
                                                </div>
                                            </MenuItem>
                                        ) : (
                                            ''
                                        )}
                                    </Menu>

                                    {lodashGet(item, ['bundleBid', 'settlement'], []).length || lodashGet(item, ['bundleBid', 'energySettlement'], []).length ? (
                                        <div
                                            onClick={() => {
                                                setModal({
                                                    status: true,
                                                    modalId: 'panic',
                                                    data: item.bundleBid,
                                                });
                                            }}
                                        >
                                            <IconComponent icon={UilBan} tooltip={'Panic'} />
                                        </div>
                                    ) : (
                                        ''
                                    )}
                                    <div
                                        onClick={() => {
                                            setModal({
                                                status: true,
                                                modalId: 'note',
                                                data: item.bundleBid,
                                            });
                                        }}
                                    >
                                        <IconComponent icon={UilNotes} tooltip={'Bid Note'} />
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            );

            return item;
        });
        if (indivisualMarket.status) setTotalRevenue(lodashGet(data, ['data', 'totalRevenue']));

        setBidsData({ bids: processedData, totalCount: lodashGet(data, ['data', 'totalCount'], 0) });
    };

    const handleDelete = () => {
        if (deleteModal.source === 'bid')
            service.DeleteBid(deleteModal.id, { type: deleteModal.type }, startLoader, () => handleDeleteSuccess(deleteModal.source), handleError, stopLoader);
        else if (deleteModal.source === 'instanceBundle')
            AdminInstanceBundleService.DeleteInstanceBundle(deleteModal.id, startLoader, () => handleDeleteSuccess(deleteModal.source), handleError, stopLoader);
    };

    const handleDeleteSuccess = (source) => {
        if (source === 'bid') getBids();
        else if (source === 'instanceBundle') getInstanceBundle();
        toast.success('Deleted Successfully');
    };

    const handleError = (err) => {
        toast.error(lodashGet(err, ['response', 'data', 'message']));
    };

    const handlePageChange = (pageNo) => {
        setPaginationQuery({ ...paginationQuery, skip: paginationQuery.limit * pageNo, page: pageNo });
    };

    const closeModal = () => {
        setModal({ status: false, modalId: null, data: {} });
    };

    const getMarketEnabledCusLicenseIns = () => {
        AdminBiddingService.GetMarketEnabledCusLicenseIns(selectedRegulation, startLoader, getMarketEnabledCusLicenseInsSuccess, handleError, stopLoader);
    };

    const getMarketEnabledCusLicenseInsSuccess = ({ data }) => {
        data = lodashGet(data, ['data'], {});
        setMarketEnabledCustomerLicenseIns(data);
        const customers = lodashKeys(data);
        if (customers.length) {
            const customerOptions = customers.map((cusId) => ({
                label: lodashGet(data, [cusId, 'name']),
                value: cusId,
                biddingMarkets: lodashGet(data, [cusId, 'biddingMarkets']),
            }));
            setCustomers(customerOptions);
            if (!selectedCustomer || initialLoad) setSelectedCustomer(customerOptions[0].value);
        } else toast.error('No Instances Enabled');
    };

    const getInstanceBundle = () => {
        AdminInstanceBundleService.GetInstanceBundle(
            { market: selectedRegulation, customerId: selectedCustomer },
            startLoader,
            handleInstanceBundleSuccess,
            handleError,
            stopLoader
        );
    };

    const handleInstanceBundleSuccess = ({ data }) => {
        const temp = lodashGet(data, ['data', 'data']).map((e) => ({
            label: e.name,
            value: e._id,
            pairs: e.pairs,
            customerId: e.customerId,
        }));
        setSelectedInstanceBundle(temp[0] || {});
        setInstanceBundles(temp || []);

        let tempAgreement = {};
        lodashGet(data, ['data', 'agreement'], []).map((agg) => {
            tempAgreement = { ...tempAgreement, ...agg.instances };
        });
        setAgreement(tempAgreement);
    };

    const checkForAggrement = () => {
        let isAggrement = { status: true, instance: [], cost: 0 };
        selectedInstanceBundle.pairs.map((ins) => {
            if (!lodashGet(agreement, [ins.instanceId])) {
                isAggrement.status = false;
                isAggrement.instance.push(ins.instanceName);
            }
            isAggrement.cost += lodashGet(agreement, [ins.instanceId, 'cost'], 0);
        });
        return isAggrement;
    };

    return (
        <div>
            <div className={FilterBarClasses.ParentWrapper} style={{ alignItems: 'center' }}>
                <div className={FilterBarClasses.Left}>
                    {isRootUser && (
                        <div className={FilterBarClasses.Filter}>
                            <label>Admin</label>
                            <div className={bidClasses.DropdownContainer}>
                                <DropdownComponent
                                    defaultValue={{ value: selectedAdmin }}
                                    options={admins}
                                    onChange={(e) => {
                                        setSelectedAdmin(e.value);
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    <div className={FilterBarClasses.Filter}>
                        <label>Customer</label>
                        <div className={bidClasses.DropdownContainer}>
                            <DropdownComponent
                                defaultValue={{ value: selectedCustomer }}
                                options={customers}
                                onChange={(e) => {
                                    setSelectedCustomer(e.value);
                                }}
                            />
                        </div>
                    </div>

                    {/* market and filter for master bids */}
                    <div className={FilterBarClasses.Filter}>
                        <label>{indivisualMarket.status ? 'Instance Bundle' : 'Market'}</label>
                        <div className={bidClasses.DropdownContainer}>
                            <DropdownComponent
                                defaultValue={indivisualMarket.status ? selectedInstanceBundle : { value: selectedRegulation }}
                                options={indivisualMarket.status ? instanceBundles : regulations}
                                onChange={(e) => {
                                    if (indivisualMarket.status) setSelectedInstanceBundle(e);
                                    else setSelectedRegulation(e.value);
                                }}
                            />
                        </div>
                    </div>
                    {!indivisualMarket.status && (
                        <div className={FilterBarClasses.Filter}>
                            <label> {'Filter'} </label>
                            <div className={bidClasses.DropdownContainer}>
                                <DropdownComponent defaultValue={{ value: selectedFilter }} options={FILTER_OPTIONS} onChange={(e) => setSelectedFilter(e.value)} />
                            </div>
                        </div>
                    )}
                </div>
                <div className={FilterBarClasses.Right} style={{ alignItems: 'center' }}>
                    <div className={FilterBarClasses.Filter}>
                        {indivisualMarket.status ? (
                            <>
                                {startDate && endDate && (
                                    <DateRangePicker
                                        startDate={startDate}
                                        endDate={endDate}
                                        onChange={(date) => {
                                            setStartDate(moment(date.startDate).toDate());
                                            setEndDate(moment(date.endDate).toDate());
                                        }}
                                        showLabels={true}
                                    />
                                )}
                            </>
                        ) : (
                            <>
                                <label>Date</label>
                                <DatePicker
                                    date={date}
                                    setDate={(d) => {
                                        setDate(d);
                                    }}
                                    showArrow={true}
                                    disableFutureDate={false}
                                />
                            </>
                        )}
                    </div>
                    <button className="btn-primary" style={{ width: 'auto' }} onClick={getBids}>
                        Submit
                    </button>
                    <button
                        className="btn-primary"
                        style={{ width: 'auto' }}
                        onClick={() => {
                            if (indivisualMarket.status) {
                                const data = checkForAggrement();
                                if (data.status)
                                    setModal({
                                        status: true,
                                        modalId: 'createBid',
                                        data: {
                                            ...(indivisualMarket.status
                                                ? {
                                                      instanceBundle: {
                                                          label: selectedInstanceBundle.label,
                                                          value: selectedInstanceBundle.value,
                                                          pairs: selectedInstanceBundle.pairs || [],
                                                      },
                                                  }
                                                : { adminId: selectedAdmin, bidDate: date }),
                                            customerId: selectedCustomer,
                                            market: selectedRegulation,
                                            cost: data?.cost,
                                        },
                                    });
                                else toast.error('No Agreement Found');
                            } else {
                                setModal({
                                    status: true,
                                    modalId: 'bulkBid',
                                    data: {
                                        adminId: selectedAdmin,
                                        bidDate: date,
                                        customerId: selectedCustomer,
                                        market: selectedRegulation,
                                    },
                                });
                            }
                        }}
                    >
                        {indivisualMarket.status ? 'Create Bid' : 'Create Bulk Bid'}
                    </button>

                    {indivisualMarket.status && (
                        <div className={bidClasses.ActionContainer}>
                            <Menu
                                menuButton={
                                    <button className="btn-primary" style={{ width: 'auto' }}>
                                        Instance Bundle
                                    </button>
                                }
                                direction={'down'}
                                arrow={true}
                                menuStyle={{ minWidth: '0' }}
                            >
                                <MenuItem
                                    onClick={() =>
                                        setModal({
                                            status: true,
                                            modalId: 'createInstanceBundle',
                                            data: {
                                                market: indivisualMarket.market,
                                                rawCustomerData: marketEnabledCustomerLicenseIns,
                                                customers: customers,
                                            },
                                        })
                                    }
                                >
                                    <div>Create Bundle</div>
                                </MenuItem>
                                {selectedInstanceBundle?.value && (
                                    <>
                                        <MenuDivider />
                                        <MenuItem
                                            onClick={() =>
                                                setModal({
                                                    status: true,
                                                    modalId: 'createInstanceBundle',
                                                    data: {
                                                        market: indivisualMarket.market,
                                                        rawCustomerData: marketEnabledCustomerLicenseIns,
                                                        customers: customers,
                                                        editBundle: selectedInstanceBundle,
                                                    },
                                                })
                                            }
                                        >
                                            <div>Edit Bundle </div>
                                        </MenuItem>
                                        <MenuDivider />
                                        <MenuItem
                                            onClick={() => {
                                                setDeleteModal({ status: true, source: 'instanceBundle', id: selectedInstanceBundle.value });
                                            }}
                                        >
                                            <div>Delete Bundle</div>
                                        </MenuItem>
                                    </>
                                )}
                            </Menu>
                        </div>
                    )}
                </div>
            </div>

            <div className={bidClasses.MainContainer}>
                <div className={bidClasses.TableHeader}>
                    <div>
                        <Typography content={heading} />
                        <div className={bidClasses.TableCount}>
                            Total Count :
                            <span>
                                <Typography size="14" content={bidsData.totalCount} />
                            </span>
                        </div>

                        {indivisualMarket.status && (
                            <>
                                <div className={bidClasses.TableCount}>
                                    Total Revenue (CM) :
                                    <span>
                                        <Typography size="14" content={totalRevenue.capacity + ' €'} />
                                    </span>
                                </div>
                                {indivisualMarket.market.includes('aFRR') || indivisualMarket.market.includes('mFRR') ? (
                                    <div className={bidClasses.TableCount}>
                                        Total Revenue (EAM) :
                                        <span>
                                            <Typography size="14" content={totalRevenue.energy + ' €'} />
                                        </span>
                                    </div>
                                ) : (
                                    ''
                                )}
                            </>
                        )}
                    </div>
                </div>
                <Table
                    head={tableHeadersAndKeys.headers}
                    keys={tableHeadersAndKeys.keys}
                    data={bidsData.bids}
                    page={paginationQuery.page}
                    Pagination={true}
                    limit={paginationQuery.limit}
                    handlePageChange={handlePageChange}
                    totalCount={bidsData.totalCount}
                />
            </div>
            {modal.status && (
                <ModalComponent isOpen={modal.status} setOpen={() => closeModal()} style={{ overflow: 'initial' }}>
                    {modal.modalId === 'bidInfo' && <BiddingInfo data={modal.data} bidType={'bundle'} />}
                    {modal.modalId === 'createBid' && <CreateBundleBid data={modal.data} closeModal={closeModal} getBids={getBids} />}
                    {modal.modalId === 'resultId' && <ResultId bid={modal.data} closeModal={closeModal} getBids={getBids} bidType={'bundle'} />}
                    {modal.modalId === 'createSecondaryBid' && <CreateSecondaryBundleBid data={modal.data} closeModal={closeModal} getBids={getBids} />}
                    {modal.modalId === 'note' && <NotesLogsIndex bid={modal.data} closeModal={closeModal} bidType={'bundle'} />}
                    {modal.modalId === 'fetchResult' && <FetchResult bid={modal.data} closeModal={closeModal} bidType={'bundle'} />}
                    {modal.modalId === 'panic' && <Panic bid={modal.data} closeModal={closeModal} getBids={getBids} bidType={'bundle'} />}
                    {modal.modalId === 'bulkBid' && <CreateBulkBundleBid data={modal.data} closeModal={closeModal} getBids={getBids} />}
                    {modal.modalId === 'updateEnergyBid' && <UpdatePostedEnergyBid bid={modal.data} closeModal={closeModal} bidType={'bundle'} />}
                    {modal.modalId === 'createInstanceBundle' && <CreateEditInstanceBundle data={modal.data} closeModal={closeModal} getInstanceBundle={getInstanceBundle} />}
                </ModalComponent>
            )}
            {deleteModal.status && (
                <DeleteModal
                    deletefunction={handleDelete}
                    opendeleteModal={deleteModal.status}
                    setOpenDeleteModal={() => setDeleteModal({ status: false, source: null, type: null, id: null })}
                />
            )}
        </div>
    );
};
